/**
 * MORVIX GRAPHICS — central site configuration.
 *
 * Everything editable lives here: contact details, social destinations,
 * external links, services, process, values, stats and portfolio projects.
 * Replace the PLACEHOLDER values below with the real destinations, and swap
 * placeholder image URLs for real supplied assets — no component changes needed.
 */

export const brand = {
  name: 'Morvix Graphics',
  short: 'Morvix',
  parent: 'Morvix Agency',
  tagline: 'Where we Bring Ideas to Life.',
  founder: 'Moses',
  founded: 2022,
  yearsLabel: '4+',
  serviceModel: 'Virtually serving clients worldwide.',
  hours: 'Open 24 hours · 24-hour support',
  /** Supplied Morvix Graphics logo (white mark — inverted automatically in light mode). */
  logo: 'https://cdn.magicpatterns.com/uploads/qi5w8iFYrDNLrvK6AhDJP7/730109467_122202755450573647_6575663323753765884_n__1___1_-removebg-preview.png',
}

/** Live Morvix Graphics contact details. */
export const contact = {
  email: 'morvixagencyofficial@gmail.com',
  /** International format, digits only. */
  whatsappNumber: '2348113101256',
  /** Human-readable format for display. */
  whatsappDisplay: '+234 811 310 1256',
  whatsappMessage: "Hi Morvix Graphics — I'd like to talk about a project.",
}

export const whatsappUrl = `https://wa.me/${contact.whatsappNumber}?text=${encodeURIComponent(
  contact.whatsappMessage,
)}`

/** Live external destinations. Linktree is a social hub only — never a portfolio/review target. */
export const links = {
  portfolio:
    'https://drive.google.com/drive/folders/1-ItP0s0HnOFutz9gAlK-be5PBqDn03QQ?usp=sharing',
  reviews: 'https://www.trustpilot.com/review/topmosmarketing.com',
  facebook: 'https://www.facebook.com/morvixgraphics',
  instagram: 'https://www.instagram.com/morvixgraphics',
  tiktok: 'https://tiktok.com/@morvixgraphics',
  pinterest: 'https://www.pinterest.com/morvixgraphics',
  linktree: 'https://linktr.ee/morvixgraphics',
}

/** Formspree form ID — endpoint https://formspree.io/f/mgogrqoz */
export const FORMSPREE_FORM_ID = 'mgogrqoz'
export const FORMSPREE_NEWSLETTER_ID = FORMSPREE_FORM_ID

export const nav = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  { label: 'Work', to: '/work' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
]

export const stats = [
  { value: 30, suffix: '+', label: 'Brands worked with' },
  { value: 400, suffix: '+', label: 'Projects completed' },
  { value: 80, suffix: '%+', label: 'Client satisfaction' },
  { value: 4, suffix: '+', label: 'Years of practice' },
]

export type Service = {
  title: string
  description: string
  category: string
  image: string
}

/** Placeholder imagery — replace `image` with real project visuals when available. */
const IMG = {
  form3d: 'https://cdn.magicpatterns.com/patterns/generated-images/410ad08e-f2c1-45dd-97df-3e45eaab432c.jpg',
  process: 'https://cdn.magicpatterns.com/patterns/generated-images/bef693dc-da4c-4c04-88ec-2cbc5ab107b4.jpg',
  guidelines: 'https://cdn.magicpatterns.com/patterns/generated-images/801cfe8c-9052-42b9-8378-b8af0ea06645.jpg',
  poster: 'https://cdn.magicpatterns.com/patterns/generated-images/b0e897ba-0bcb-42a6-8010-2409f4d579b7.jpg',
  packaging: 'https://cdn.magicpatterns.com/patterns/generated-images/ff0a0722-9ba0-4f19-a322-e8b4060197de.jpg',
  /** Supplied client asset — Wayne & Co brand identity process / system screenshot. */
  brandIdentity:
    'https://cdn.magicpatterns.com/uploads/8LdGzr2eYHFdpEXWnvmPiN/ChatGPT_Image_Jul_28,_2026,_01_30_47_PM.png',
  /* Supplied service visuals — one per discipline. */
  packagingReal: 'https://cdn.magicpatterns.com/uploads/vir5xBPv2idhCofuzeDRZm/colin00b-cigarettes-2469361_1920.jpg',
  flyerReal: 'https://cdn.magicpatterns.com/uploads/3WaXC9snbh5mfwXNVCqqnt/b458301a474f4ba49914c9bab0ea231d~tplv-photomode-image.jpg',
  motionReal: 'https://cdn.magicpatterns.com/uploads/di3zsiH9wmoZnkSnAwrxWx/haninabz-meditation-8389494_1920.jpg',
  uiReal: 'https://cdn.magicpatterns.com/uploads/2wJZHimzGdpgQ6Rbnu12HP/images_(11).jpg',
  webReal: 'https://cdn.magicpatterns.com/uploads/5SkdBxaAUcE6tU5jNsW9GL/Web-designer-1.webp',
  presentationReal: 'https://cdn.magicpatterns.com/uploads/kYx2tbJrrZTvAMZddwim1W/50934e87376a795143ebd6a97a2f73a9.jpg',
  bookCoverReal: 'https://cdn.magicpatterns.com/uploads/x3n9DiFThMPLsaPFJGydxc/images_(8).jpg',
  labelReal: 'https://cdn.magicpatterns.com/uploads/7U5EcgdcLWNeDGnoSk6LPx/images_(7).jpg',
  illustrationReal: 'https://cdn.magicpatterns.com/uploads/gcCKUXy2EtgY7x5SU6mvzW/images_(10).jpg',
  mockupReal: 'https://cdn.magicpatterns.com/uploads/eKxvJWEf1Ah92NSopVygAV/images_(5).jpg',
}

export const images = IMG

export const services: Service[] = [
  {
    title: 'Brand Identity',
    description: 'Complete visual identities designed to create memorable and consistent brands.',
    category: 'Identity',
    image: 'https://cdn.magicpatterns.com/uploads/cVFoRX3ZoykR4bxrK5NZKq/ChatGPT_Image_Jul_28,_2026,_01_30_47_PM.png',
  },
  {
    title: 'Logo Design',
    description: 'Distinctive logos designed around the personality and positioning of a brand.',
    category: 'Identity',
    image: IMG.process,
  },
  {
    title: 'Packaging Design',
    description: 'Packaging systems designed to attract attention and communicate value.',
    category: 'Print',
    image: 'https://cdn.magicpatterns.com/uploads/b7ycM2EpFFF3CsG3NtgCWN/custom-packaging.webp',
  },
  {
    title: 'Brand Guidelines',
    description: 'Complete guidelines that define how a brand should look and communicate.',
    category: 'Identity',
    image: IMG.guidelines,
  },
  {
    title: 'Print Design',
    description: 'Professional print materials for businesses and campaigns.',
    category: 'Print',
    image: IMG.poster,
  },
  {
    title: 'Social Media Design',
    description: 'Creative social media visuals designed for consistency and engagement.',
    category: 'Digital',
    image: 'https://cdn.magicpatterns.com/uploads/wLfGzzo7iDGJNtkRfkkQb1/4331759572b3db22cfd674a1377b5a96.jpg',
  },
  {
    title: 'Motion Graphics',
    description: 'Motion design and animated visuals that bring brand stories to life.',
    category: 'Motion',
    image: 'https://cdn.magicpatterns.com/uploads/75888JcJWPxXxsTswRieR5/images_(12).jpg',
  },
  {
    title: 'UI Design',
    description: 'Digital interface design focused on clarity and usability.',
    category: 'Digital',
    image: IMG.uiReal,
  },
  {
    title: 'Web Design',
    description: 'Modern website design focused on aesthetics, experience and conversion.',
    category: 'Digital',
    image: IMG.webReal,
  },
  {
    title: 'Presentation Design',
    description: 'Professional presentations designed to communicate ideas clearly and persuasively.',
    category: 'Digital',
    image: IMG.presentationReal,
  },
  {
    title: 'Book Covers',
    description: 'Creative covers designed to create a strong first impression.',
    category: 'Print',
    image: IMG.bookCoverReal,
  },
  {
    title: 'Flyers',
    description: 'Promotional and informational flyer design.',
    category: 'Print',
    image: IMG.flyerReal,
  },
  {
    title: 'Product Labels',
    description: 'Professional product labels that strengthen product presentation.',
    category: 'Print',
    image: IMG.labelReal,
  },
  {
    title: 'Illustration & Art',
    description: 'Custom illustrations and visual artwork.',
    category: 'Art',
    image: IMG.illustrationReal,
  },
  {
    title: 'Mockup Creation',
    description: 'Professional mockups for presenting branding and products.',
    category: 'Art',
    image: IMG.mockupReal,
  },
  {
    title: 'Clothing Design',
    description: 'Creative visual designs for clothing and apparel.',
    category: 'Apparel',
    image: 'https://cdn.magicpatterns.com/uploads/9Z5MBpnyeEZQEQjuPeqYmh/7a683ffe5ceb5f47bfde67705741edd2.jpg',
  },
]

export const serviceOptions = services.map((s) => s.title)

export const values = [
  { name: 'Creativity', note: 'Ideas before decoration.' },
  { name: 'Excellence', note: 'Nothing ships half-finished.' },
  { name: 'Innovation', note: 'New answers, not recycled ones.' },
  { name: 'Authenticity', note: 'Your brand, not a trend.' },
  { name: 'Strategy', note: 'Every choice has a reason.' },
  { name: 'Simplicity', note: 'Clarity is the hardest craft.' },
  { name: 'Speed', note: 'Momentum without shortcuts.' },
  { name: 'Quality', note: 'Details decide credibility.' },
  { name: 'Trust', note: 'Straight answers, always.' },
]

export const process = [
  {
    step: '01',
    title: 'Discover',
    description: 'We understand the business, the audience, the goals and the real challenges behind the brief.',
  },
  {
    step: '02',
    title: 'Strategize',
    description: 'We define the creative direction and the visual strategy the work will be built on.',
  },
  {
    step: '03',
    title: 'Create',
    description: 'Strategy becomes strong visual concepts — form, type, colour, system.',
  },
  {
    step: '04',
    title: 'Refine',
    description: 'We sharpen the work through thoughtful iteration until every detail earns its place.',
  },
  {
    step: '05',
    title: 'Deliver',
    description: 'Polished, organised, production-ready assets handed over properly.',
  },
  {
    step: '06',
    title: 'Launch',
    description: 'We help the brand carry the work into the world with confidence.',
  },
]

export type Project = {
  slug: string
  title: string
  subtitle: string
  categories: string[]
  year: string
  cover: string
  gallery: { src: string; caption: string; span?: 'full' | 'half' }[]
  intro: string
  scope: string[]
  /** Optional longer narrative shown under the intro. */
  description?: string
  /** Optional project highlights block (services + deliverables). */
  services?: string[]
  deliverables?: string[]
}

/**
 * Real projects only. Add new entries here and they appear across the site
 * automatically (portfolio grid, filters and featured slot).
 */
export const projects: Project[] = [
  {
    slug: 'apparel-identity',
    title: 'Apparel Identity System',
    subtitle: 'Monogram identity for a clothing label',
    categories: ['Branding', 'Logo Design', 'Apparel'],
    year: '2025',
    cover: 'https://cdn.magicpatterns.com/uploads/9Z5MBpnyeEZQEQjuPeqYmh/7a683ffe5ceb5f47bfde67705741edd2.jpg',
    intro:
      'A monogram-led identity built for apparel: one geometric mark engineered to hold up embroidered on a cap, printed on cotton, debossed on leather or reduced to a single centimetre on a watch dial.',
    scope: ['Logo & monogram construction', 'Apparel application system', 'Mockup art direction', 'Usage rules'],
    gallery: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/q77qgrSpFzL4vgxB7EaMJx/3b3351ff7523b26de9f471eea2d1c705.jpg',
        caption: 'Primary mark — geometric monogram in containment circle',
        span: 'half',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/9Z5MBpnyeEZQEQjuPeqYmh/7a683ffe5ceb5f47bfde67705741edd2.jpg',
        caption: 'Mockup 01 — embroidered cap & chest print',
        span: 'half',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/4DHqXwuzEtjn1MmWDd8K9i/8925efd9e5e8299b0b53f4fb7f9dd9dc.jpg',
        caption: 'Mockup 02 — the identity worn in the world',
        span: 'full',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/4RSqkXcG41kesYUr7RJTpc/5860da05ef1135254cca93631bed5638.jpg',
        caption: 'Mockup 03 — footwear & accessory application',
        span: 'full',
      },
      {
        src: IMG.process,
        caption: 'Behind the scenes — mark construction (placeholder, replace with process screenshot)',
        span: 'half',
      },
    ],
  },
  {
    slug: 'morvix-monogram-identity',
    title: 'Miceen Apparel Identity',
    subtitle: 'Brand identity, logo system & brand applications',
    categories: ['Branding', 'Logo Design', 'Apparel'],
    year: '2025',
    cover: 'https://cdn.magicpatterns.com/uploads/rXnz9dUxJqugTcXsteAdPx/5860da05ef1135254cca93631bed5638.jpg',
    intro:
      'A bold monogram identity created to demonstrate how a simple geometric logo system can scale seamlessly across apparel, accessories and premium lifestyle products while maintaining strong brand recognition.',
    description:
      'This identity explores the power of a minimal monogram built around bold geometric forms. The project focuses on creating a versatile logo system that remains instantly recognizable across multiple brand touchpoints — from clothing and merchandise to premium accessories. The result is a timeless visual identity designed for consistency, flexibility and long-term brand recognition.',
    scope: ['Brand Identity', 'Logo System', 'Visual Identity', 'Brand Applications'],
    services: ['Brand Identity', 'Logo Design', 'Visual System', 'Brand Applications'],
    deliverables: [
      'Primary Logo',
      'Brand Mark',
      'Merchandise Mockups',
      'Product Applications',
      'Identity System',
    ],
    gallery: [
      {
        src: 'https://cdn.magicpatterns.com/uploads/pPj6RozhKVkpPHZs1KdCbr/3b3351ff7523b26de9f471eea2d1c705.jpg',
        caption: 'Primary monogram logo designed for maximum recognition and scalability',
        span: 'half',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/8ACJsTPvSPg3qmwqb7VQcu/8925efd9e5e8299b0b53f4fb7f9dd9dc.jpg',
        caption:
          'Application of the identity across branded apparel to demonstrate consistency in everyday use',
        span: 'half',
      },
      {
        src: 'https://cdn.magicpatterns.com/uploads/rXnz9dUxJqugTcXsteAdPx/5860da05ef1135254cca93631bed5638.jpg',
        caption:
          'The identity extends naturally to premium products such as footwear and accessories while maintaining a refined visual presence',
        span: 'full',
      },
    ],
  },
]

export const featuredProject = projects[0]

/**
 * Client wall. `src` = supplied logo asset; `src: null` renders the client
 * name as a typographic wordmark (never a fabricated logo).
 */
export const clientLogos: { name: string; src: string | null; note?: string }[] = [
  {
    name: 'Apparel Label',
    src: 'https://cdn.magicpatterns.com/uploads/q77qgrSpFzL4vgxB7EaMJx/3b3351ff7523b26de9f471eea2d1c705.jpg',
    note: 'Identity & apparel system',
  },
  { name: 'Wayne & Co', src: null, note: 'Brand identity & guidelines' },
  { name: 'Luxe Crown Studio', src: null, note: 'Identity & print' },
  { name: 'Sonni LTD', src: null, note: 'Logo & brand system' },
]

export const seo = {
  defaultTitle: 'Morvix Graphics — Creative Agency & Brand Identity Design',
  defaultDescription:
    'Morvix Graphics is a creative agency building brand identities, logos, packaging and digital experiences for ambitious brands worldwide. Where we bring ideas to life.',
  keywords:
    'graphic design, brand identity, logo design, creative agency, packaging design, brand strategy',
}
